import * as React from 'react';
import BPromise from 'bluebird';
import moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import {
  ColumnDefinition,
  getFilters2,
  getPagesCount,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { ResizablePanel } from 'Src/components/ResizablePanel/ResizablePanel';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import { getSeason } from 'Src/helpers/accessor/accessor';
import { Championship } from 'Src/models/championship';
import {
  createChampionship,
  deleteChampionship,
  getAllChampionships,
  getChampionships,
  getChampionshipsCount,
  updateChampionship
} from 'Src/helpers/service/adminSU/championshipsTable';
import { CHAMPIONSHIP_TABS } from 'Src/consts/championship';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import { ChampionshipSummary } from 'Src/components/ChampionshipSummary/ChampionshipSummary';
import { ChampionshipForm } from './ChampionshipForm/ChampionshipForm';
import { Button } from 'Src/components/Button/Button';
import { getSelectOptionForSeason } from 'Src/helpers/table/select';
import { ChampionshipPublicSiteSettingsForm } from './ChampionshipPublicSiteSettingsForm/ChampionshipPublicSiteSettingsForm';
import { ChampionshipPublicSiteSettingsSummary } from 'Src/components/ChampionshipPublicSiteSettingsSummary/ChampionshipPublicSiteSettingsSummary';
import { ChampionshipTournamentsSummary } from 'Src/components/ChampionshipTournamentsSummary/ChampionshipTournamentsSummary';
import { getIsPromoteTournament, getIsPublishOnTheWebsite } from '../../../../helpers/accessor/accessor';
import { getSelectOptionForBooleanQuestion } from '../../../../helpers/table/select';
import './Championships.scss';
import '../Tournaments/Tournaments.css';

const getChampionshipSportColor = (key: string): string => {
  const hash = String(key || 'sport')
    .split('')
    .reduce((value, char) => ((value << 5) - value + char.charCodeAt(0)) | 0, 0);
  return `hsl(${Math.abs(hash) % 360}, 58%, 47%)`;
};

const getChampionshipTournamentCount = (championship: Championship): number => {
  const tournamentIdsCount = Array.isArray(championship.tournamentIds) ? championship.tournamentIds.length : 0;
  const tournamentsCount = Array.isArray(championship.tournaments) ? championship.tournaments.length : 0;
  return Math.max(tournamentIdsCount, tournamentsCount);
};

const ChampionshipNameCell = (championship): React.ReactElement => {
  const sportName = championship.sport?.name || 'Sport not set';
  return <td>
    <div className="utListTournament championshipListName">
      <div className="utListTournament__title">
        {championship.name || 'Unnamed championship'}
        {championship.isPublishOnTheWebsite ? <span className="utListStateIcon is-published" title="Published on the public website" aria-label="Published on the public website">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3.3 3 14.7 0 18M12 3c-3 3.3-3 14.7 0 18" /></svg>
        </span> : null}
        {championship.isPromoteTournament ? <span className="utListStateIcon is-highlighted" title="Highlighted championship" aria-label="Highlighted championship">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-2.9-5.6 2.9 1.1-6.2L3 9.6l6.2-.9L12 3Z" /></svg>
        </span> : null}
      </div>
      <div className="utListTournament__meta">
        <i style={{ backgroundColor: getChampionshipSportColor(championship.sport?.id || championship.sportId || sportName) }} />
        <span>{sportName}</span>
      </div>
    </div>
  </td>;
};

const ChampionshipTournamentCountCell = (championship, options, onOpen): React.ReactElement => {
  const count = getChampionshipTournamentCount(championship);
  return <td><button type="button" className="championshipTournamentCount" onClick={event => { event.stopPropagation(); onOpen(championship, 'tournaments'); }}>{count}<span>View</span></button></td>;
};

const ChampionshipStartCell = (championship): React.ReactElement => {
  const start = moment(championship.startTime);
  return <td><div className="utListDate"><strong>{start.isValid() ? start.format('DD MMM YYYY') : 'Date not set'}</strong><span>{start.isValid() ? start.format('HH:mm') : 'Time not set'}</span></div></td>;
};

const ChampionshipEndCell = (championship): React.ReactElement => {
  const end = moment(championship.endTime);
  return <td><div className="utListDate"><strong>{end.isValid() ? end.format('DD MMM YYYY') : 'Date not set'}</strong><span>{end.isValid() ? end.format('HH:mm') : 'Time not set'}</span></div></td>;
};

const mergeChampionshipStatusDate = (existingEndTime, isArchive: boolean, boundary: Date) => {
  const existing = existingEndTime || {};
  if (isArchive) return { ...existing, $lt: boundary };
  const selectedFrom = existing.$gte;
  return {
    ...existing,
    $gte: selectedFrom && new Date(selectedFrom).getTime() > boundary.getTime() ? selectedFrom : boundary
  };
};

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    cell: ChampionshipNameCell
  },
  {
    text: 'Tournaments',
    field: 'tournamentsCount',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: ChampionshipTournamentCountCell
  },
  {
    text: 'Season',
    field: 'season',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getSeason,
    filterControl: 'select'
  },
  {
    text: 'Start',
    field: 'startTime',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    cell: ChampionshipStartCell
  },
  {
    text: 'End',
    field: 'endTime',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    cell: ChampionshipEndCell
  },
  {
    text: 'Published',
    field: 'isPublishOnTheWebsite',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: getIsPublishOnTheWebsite
  },
  {
    text: 'Highlighted',
    field: 'isPromoteTournament',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: getIsPromoteTournament
  }
];

type ChampionshipHeaderSection = 'upcoming' | 'archive';

interface ChampionshipListHeaderProps {
  user: AppUser;
  history: History;
  activeSection: ChampionshipHeaderSection;
  onAddChampionship: () => void;
}

interface ChampionshipListHeaderState {
  upcomingCount?: number;
  archiveCount?: number;
}

const normalizeChampionshipCount = (result: any): number => {
  const rawCount = result && result.count;
  const value = Number(rawCount && typeof rawCount === 'object' ? rawCount.count : rawCount);
  return isFinite(value) ? value : 0;
};

class ChampionshipsListHeader extends React.PureComponent<ChampionshipListHeaderProps, ChampionshipListHeaderState> {
  state: ChampionshipListHeaderState = {};

  componentDidMount() {
    const boundary = new Date();
    boundary.setHours(0, 0, 0, 0);
    getChampionshipsCount(this.props.user, { endTime: { $gte: boundary } })
      .then(result => this.setState({ upcomingCount: normalizeChampionshipCount(result) }))
      .catch(() => undefined);
    getChampionshipsCount(this.props.user, { endTime: { $lt: boundary } })
      .then(result => this.setState({ archiveCount: normalizeChampionshipCount(result) }))
      .catch(() => undefined);
  }

  render() {
    const { activeSection, history, onAddChampionship } = this.props;
    const { upcomingCount, archiveCount } = this.state;
    return <div className="unionTournamentsHeader unionTournamentsPageHeader championshipsPageHeader">
      <h1>Championships</h1>
      <nav className="unionTournamentsTabs" aria-label="Championship lists">
        <button type="button" className={activeSection === 'upcoming' ? 'isActive' : ''} onClick={() => history.push('/championships/upcoming')}>
          Upcoming {typeof upcomingCount === 'number' ? <span>{upcomingCount}</span> : null}
        </button>
        <button type="button" className={activeSection === 'archive' ? 'isActive' : ''} onClick={() => history.push('/championships/archive')}>
          Past {typeof archiveCount === 'number' ? <span>{archiveCount}</span> : null}
        </button>
      </nav>
      <button type="button" className="unionTournamentsAdd" onClick={onAddChampionship}>+ Add championship</button>
    </div>;
  }
}

interface State {
  items: Championship[];
  currentPage: number;
  selectedItems: Championship[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  isDeleteChampionshipModalOpen: boolean;
  currentTabIndex: number;
  isEditable: boolean;
  isEditablePublicSiteSettings: boolean;
  tournamentsModalChampionship?: Championship;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Championships extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      isDeleteChampionshipModalOpen: false,
      currentTabIndex: 0,
      isEditable: false,
      isEditablePublicSiteSettings: false,
      tournamentsModalChampionship: undefined
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.location.search !== this.props.location.search ||
      nextProps.location.pathname !== this.props.location.pathname
    ) {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps);
    }
  }

  setItems(props = this.props) {
    const { history, user } = props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const filterWhere = getServerFieldSectionWhere(filters, { isCorrectTime: true });
    const isArchive = history.location.pathname.indexOf('/archive') !== -1;
    const boundary = new Date();
    boundary.setHours(0, 0, 0, 0);
    const existingEndTime = filterWhere.endTime || {};
    const where = {
      ...filterWhere,
      endTime: mergeChampionshipStatusDate(existingEndTime, isArchive, boundary)
    };

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getItemsPromise = getChampionships(user, serverQueryFilter);
    const getItemsCountPromise = getChampionshipsCount(user, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return BPromise.all(promises).then(([countObj, championship]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: championship,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemSelect = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
      currentTabIndex: 0
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const filterWhere = getServerFieldSectionWhere(filters);
    const boundary = new Date();
    boundary.setHours(0, 0, 0, 0);
    const isArchive = this.props.location.pathname.indexOf('/archive') !== -1;
    const where = {
      ...filterWhere,
      endTime: mergeChampionshipStatusDate(filterWhere.endTime, isArchive, boundary)
    };

    getAllChampionships(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (options && options.modernRange) {
      nextFilters = {
        ...nextFilters,
        [filterField]: options.modernRange
      };
    } else if (options && options.modernMultiValue) {
      nextFilters = {
        ...nextFilters,
        [filterField]: Array.isArray(filterValue) ? filterValue : []
      };
    } else if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false,
      filters: {}
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onClearIndividualFilter = (filterField: string): void => {
    const { filters, sortDirection, sortColumnsName, basePath } = this.state;
    const nextFilters = { ...filters };
    delete nextFilters[filterField];

    const search: string[] = [];
    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }
    if (isFilterExist2(nextFilters)) {
      search.push(getSearchFilter(nextFilters));
    }

    this.setState({
      filters: nextFilters,
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: basePath,
      search: search.join('&')
    });
  };

  onTableFilterClick = (event): void => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  renderDeleteChampionshipModal(): React.ReactNode {
    const { selectedItems, isDeleteChampionshipModalOpen } = this.state;
    const blockedChampionships = selectedItems.filter(championship => getChampionshipTournamentCount(championship) > 0);
    const isBlocked = blockedChampionships.length > 0;

    return (
      <SimpleModal
        isOpen={isDeleteChampionshipModalOpen}
        title={isBlocked
          ? selectedItems.length === 1 ? 'Championship cannot be deleted' : 'Championships cannot be deleted'
          : selectedItems.length === 1 ? 'Delete championship?' : 'Delete championships?'}
        buttonCancelText={isBlocked ? 'Close' : 'Cancel'}
        buttonText={isBlocked ? undefined : selectedItems.length === 1 ? 'Delete championship' : 'Delete championships'}
        onButtonClick={isBlocked ? undefined : this.onDeleteChampionshipClick}
        onCloseClick={this.deleteChampionshipModalClose}
        customClass="unionTournamentDeleteModal"
        dialogStyle={{ maxWidth: 520 }}
        isBodyBold={false}
      >
        <div className="unionTournamentDeleteModal__body">
          <div className="unionTournamentDeleteModal__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 8v5" />
              <path d="M12 17h.01" />
              <path d="M10.3 3.9 2.4 17.6A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.4L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            </svg>
          </div>
          <div className="unionTournamentDeleteModal__copy">
            {isBlocked ? <>
              <strong>{blockedChampionships.length === 1
                ? 'This championship still contains tournaments.'
                : 'Some selected championships still contain tournaments.'}</strong>
              <p>Remove every tournament from the championship before deleting it.</p>
              {blockedChampionships.map(championship => {
                const count = getChampionshipTournamentCount(championship);
                return <div key={championship.id} className="unionTournamentDeleteModal__blocker">
                  <b>{championship.name}</b>
                  <ul><li>{count} tournament{count === 1 ? '' : 's'} included</li></ul>
                </div>;
              })}
            </> : <>
              <strong>{selectedItems.length === 1
                ? selectedItems[0].name
                : `${selectedItems.length} selected championships`}</strong>
              <p>This action permanently deletes the selected championship data and cannot be undone.</p>
            </>}
          </div>
        </div>
      </SimpleModal>
    );
  }

  renderTournamentsModal(): React.ReactNode {
    const championship = this.state.tournamentsModalChampionship;
    const tournaments = championship ? championship.tournaments || [] : [];
    return <SimpleModal isOpen={Boolean(championship)} title={championship ? `${championship.name} tournaments` : 'Championship tournaments'} showFooter={false} customClass="championshipTournamentsModal" dialogStyle={{ maxWidth: 460 }} onCloseClick={() => this.setState({ tournamentsModalChampionship: undefined })} onBackdropClick={() => this.setState({ tournamentsModalChampionship: undefined })}>
      <div className="championshipTournamentsModal__body">
        <div className="championshipTournamentsModal__summary"><strong>{tournaments.length} tournament{tournaments.length === 1 ? '' : 's'}</strong><span>currently included in this championship</span></div>
        {tournaments.length ? <div className="championshipTournamentsModal__list">{tournaments.map(tournament => <article key={tournament.id}><div><strong>{tournament.name}</strong><span>{tournament.sport?.name || (championship as any).sport?.name || 'Sport not set'}</span></div><time>{tournament.startTime ? new Date(tournament.startTime).toLocaleDateString() : 'Date not set'}</time></article>)}</div> : <div className="championshipTournamentsModal__empty"><span>◎</span><strong>No tournaments added yet</strong><p>Use Manage tournaments to add eligible tournaments.</p></div>}
      </div>
    </SimpleModal>;
  }

  onChampionshipCellClick = championship => this.setState({ tournamentsModalChampionship: championship });

  onDeleteChampionshipClick = () => {
    const { selectedItems } = this.state;
    if (selectedItems.some(championship => getChampionshipTournamentCount(championship) > 0)) {
      return;
    }
    const user = this.props.user;
    const championshipIdList = selectedItems.map(item => item.id);

    this.setState({
      isLoading: true
    });

    BPromise.all(championshipIdList.map(championshipId => deleteChampionship(user, championshipId))).then(res => {
      this.setState({
        isDeleteChampionshipModalOpen: false,
        isSelectAllChecked: false,
        selectedItems: []
      });
      this.setItems();
    });
  };

  deleteChampionshipModalClose = (): void => {
    this.setState({
      isDeleteChampionshipModalOpen: false
    });
  };

  renderFormModal(): React.ReactNode {
    const { isEditable, selectedItems } = this.state;
    const { user } = this.props;

    switch (selectedItems.length) {
      case 0:
        return (
          <SimpleModal isOpen={isEditable}>
            <ChampionshipForm user={user} onCancel={this.onCancelButtonClick} onSubmit={this.onSubmitForm} />
          </SimpleModal>
        );
      case 1:
        return (
          <SimpleModal isOpen={isEditable}>
            <ChampionshipForm
              user={user}
              championship={selectedItems[0]}
              onCancel={this.onCancelButtonClick}
              onSubmit={this.onSubmitForm}
            />
          </SimpleModal>
        );
    }
  }

  onSubmitForm = (data): void => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    this.setState({
      isLoading: true
    });

    if (selectedItems.length === 0) {
      createChampionship(user, data).then(championship => {
        this.setState({
          isEditable: false,
          selectedItems: [championship]
        });
        //go to last page
        this.setItems().then(res => {
          const { total } = this.state;
          const lastPage = getPagesCount(total, LIMIT);
          this.setCurrentPageParams(lastPage);
        });
      });
    } else {
      const championshipId = selectedItems[0].id;

      updateChampionship(user, data, championshipId).then(championshipUpdated => {
        this.setState({
          isEditable: false,
          isEditablePublicSiteSettings: false,
          selectedItems: [championshipUpdated]
        });

        this.setItems();
      });
    }
  };

  onCancelButtonClick = (): void => {
    this.setState({
      isEditable: false
    });
  };

  onCreateChampionshipButtonClick = (): void => {
    this.props.history.push('/championships/create');
  };

  onItemClick = (index: number): void => {
    const championship = this.state.items[index];
    if (championship) {
      this.props.history.push(`/championships/${championship.id}/manage`);
    }
  };

  onCancelFilterClick = (): void => {
    const appliedFilters = getFilters2(COLUMNS, parse(this.props.history.location.search));
    this.setState({
      isShowFilter: false,
      filters: appliedFilters
    });
  };

  onEditChampionshipButtonClick = (): void => {
    if (this.state.selectedItems.length === 1) {
      this.props.history.push(`/championships/${this.state.selectedItems[0].id}/edit`);
    }
  };

  onManageChampionshipButtonClick = (): void => {
    if (this.state.selectedItems.length === 1) {
      this.props.history.push(`/championships/${this.state.selectedItems[0].id}/manage`);
    }
  };

  deleteChampionshipModalOpen = (): void => {
    this.setState({
      isDeleteChampionshipModalOpen: true
    });
  };

  onCancelPublicSiteSettingsButtonClick = (): void => {
    this.setState({
      isEditablePublicSiteSettings: false
    });
  };

  onEditPublicSiteSettingsButtonClick = (): void => {
    this.setState({
      isEditablePublicSiteSettings: true
    });
  };

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  getSummaryTabs(): React.ReactNode {
    const { currentTabIndex, selectedItems } = this.state;
    const { user } = this.props;

    switch (currentTabIndex) {
      case 0:
        return <ChampionshipSummary championship={selectedItems[0]} user={user} />;
      case 1:
        return <ChampionshipTournamentsSummary championship={selectedItems[0]} />;
      case 2:
        return <ChampionshipPublicSiteSettingsSummary user={user} championship={selectedItems[0]} />;
    }
  }

  onTabClick = (event, index: number) => {
    event.preventDefault();

    this.setState({
      currentTabIndex: index
    });
  };

  onEditChampionship = (value, field) => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    this.setState({
      isLoading: true
    });

    BPromise.all(
      selectedItems.map(tournament => {
        const dataCopy = { [field]: value };
        return updateChampionship(user, dataCopy, tournament.id);
      })
    )
      .then(res => {
        this.setState({
          selectedItems: []
        });
        this.setItems();
      })
      .catch(error => {
        console.error(error.response.data.details.text);
      });
  };

  renderSummary(): React.ReactNode {
    const isOneItemSelected = this.isOneItemSelected();

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select a championship above</div>;
    }

    const { currentTabIndex, selectedItems } = this.state;

    const championship = selectedItems[0];

    const summaryTabs = this.getSummaryTabs();
    const isShowEditButton = currentTabIndex === 0;
    const isShowPublicSiteSettingsButton = currentTabIndex === 2;

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={CHAMPIONSHIP_TABS} currentTabIndex={currentTabIndex}>
        {isShowEditButton && (
          <Button onClick={this.onEditChampionshipButtonClick} text={'Edit championship'} customClass={'mt-3 mb-3'} />
        )}
        {isShowPublicSiteSettingsButton && (
          <Button
            onClick={this.onEditPublicSiteSettingsButtonClick}
            text={'Edit championship website settings'}
            customClass={'mt-3 mb-3'}
          />
        )}
        {summaryTabs}
      </GenericSummary>
    );
  }

  renderPublicSiteSettingsModal() {
    const { isEditablePublicSiteSettings, selectedItems } = this.state;
    const { user } = this.props;

    if (!isEditablePublicSiteSettings || selectedItems.length !== 1) return null;

    return (
      <SimpleModal isOpen title={'Championship website & TV settings'} customClass="championshipPublishingEditor" dialogStyle={{ maxWidth: 1120 }}>
        <ChampionshipPublicSiteSettingsForm
          user={user}
          championship={selectedItems[0]}
          onCancel={this.onCancelPublicSiteSettingsButtonClick}
          onSubmit={this.onSubmitForm}
        />
      </SimpleModal>
    );
  }

  renderGrid() {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total
    } = this.state;

    const isAllYesPromoteTournamentSelectedItems = selectedItems.every(item => item.isPromoteTournament);
    const isAllNoPromoteTournamentSelectedItems = selectedItems.every(item => !item.isPromoteTournament);
    const isAllYesPublishOnTheWebsiteSelectedItems = selectedItems.every(item => item.isPublishOnTheWebsite);
    const isAllNoPublishOnTheWebsiteSelectedItems = selectedItems.every(item => !item.isPublishOnTheWebsite);

    const actionItems = [
      { itemText: 'Edit championship', onItemClick: this.onEditChampionshipButtonClick, isActive: selectedItems.length === 1 },
      { itemText: 'Manage tournaments', onItemClick: this.onManageChampionshipButtonClick, isActive: selectedItems.length === 1 },
      { itemText: 'Website & TV settings', onItemClick: this.onEditPublicSiteSettingsButtonClick, isActive: selectedItems.length === 1 },
      {
        itemText: selectedItems.length === 1 ? 'Delete championship' : 'Delete championships',
        onItemClick: this.deleteChampionshipModalOpen,
        isActive: selectedItems.length > 0
      },
      {
        itemText:
          isAllYesPublishOnTheWebsiteSelectedItems && !isAllNoPublishOnTheWebsiteSelectedItems
            ? 'Unpublish'
            : 'Publish',
        onItemClick: () =>
          this.onEditChampionship(
            !isAllYesPublishOnTheWebsiteSelectedItems && isAllNoPublishOnTheWebsiteSelectedItems,
            'isPublishOnTheWebsite'
          ),
        isActive:
          selectedItems.length > 0 &&
          (isAllYesPublishOnTheWebsiteSelectedItems || isAllNoPublishOnTheWebsiteSelectedItems)
      },
      {
        itemText:
          isAllYesPromoteTournamentSelectedItems && !isAllNoPromoteTournamentSelectedItems
            ? 'Unhighlight'
            : 'Highlight',
        onItemClick: () =>
          this.onEditChampionship(
            !isAllYesPromoteTournamentSelectedItems && isAllNoPromoteTournamentSelectedItems,
            'isPromoteTournament'
          ),
        isActive:
          selectedItems.length > 0 && (isAllYesPromoteTournamentSelectedItems || isAllNoPromoteTournamentSelectedItems)
      }
    ];

    const filterOptions = {
      season: getSelectOptionForSeason(),
      isPublishOnTheWebsite: getSelectOptionForBooleanQuestion(),
      isPromoteTournament: getSelectOptionForBooleanQuestion()
    };

    const isArchive = this.props.location.pathname.indexOf('/archive') !== -1;
    const visibleActions = actionItems.filter(item => item.isActive);
    return (
      <div className="unionTournamentsModern unionTournamentsListShell championshipsListShell">
        <ChampionshipsListHeader
          key={isArchive ? 'archive' : 'upcoming'}
          user={this.props.user}
          history={this.props.history}
          activeSection={isArchive ? 'archive' : 'upcoming'}
          onAddChampionship={this.onCreateChampionshipButtonClick}
        />
        <div className="unionTournamentsListCard championshipsListCard">
          <Grid2
            user={this.props.user}
            dataItems={items}
            filters={filters}
            currentPage={currentPage}
            total={total}
            isSelectAllChecked={isSelectAllChecked}
            isDataFiltered={isDataFiltered}
            sortDirection={sortDirection}
            sortColumnsName={sortColumnsName}
            isShowFilter={isShowFilter}
            dataItemsSelected={selectedItems}
            columns={COLUMNS}
            actionItems={visibleActions}
            isCSV={false}
            options={filterOptions}
            onItemClick={this.onItemClick}
            onItemSelect={this.onItemSelect}
            onCellIconClick={this.onChampionshipCellClick}
            onSortClick={this.onTableSortClick}
            onApplyFilterClick={this.onApplyFilterClick}
            onClearFilterClick={this.onClearFilterClick}
            onClearIndividualFilter={this.onClearIndividualFilter}
            onTableFilterChange={this.onTableFilterChange}
            onTableFilterClick={this.onTableFilterClick}
            onCancelFilterClick={this.onCancelFilterClick}
            setCurrentPageParams={this.setCurrentPageParams}
            onSelectAllClick={this.onSelectAllClick}
            onSelectAllOnPageClick={this.onSelectAllOnPageClick}
            onUnselectAllClick={this.onUnselectAllClick}
            gridTitle={`${total} championship${total === 1 ? '' : 's'}`}
            toolbarSearchField="name"
            toolbarSearchPlaceholder="Search championships..."
            onToolbarSearchClear={() => this.onClearIndividualFilter('name')}
            hiddenFilterFields={['name']}
            filterOnlyFields={['isPublishOnTheWebsite', 'isPromoteTournament']}
            isActionsInline
            selectionActionsBelow
            defaultHiddenColumns={['endTime']}
            fixedColumnFields={['name']}
            columnVisibilityKey={isArchive ? 'union-past-championships' : 'union-upcoming-championships'}
            emptyText="No championships found"
            emptyHint="Try changing the filters or create a new championship."
          />
        </div>
      </div>
    );
  }

  render() {
    const { isLoading, isEditable, isDeleteChampionshipModalOpen, isEditablePublicSiteSettings } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isEditable || isDeleteChampionshipModalOpen || isEditablePublicSiteSettings ? 'modal-open' : '';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderDeleteChampionshipModal()}
        {this.renderTournamentsModal()}
        {this.renderPublicSiteSettingsModal()}
        {this.renderGrid()}
      </div>
    );
  }
}

